import { useState, useEffect } from 'react';
import { supabase } from '~/services/supabase';

export interface IHubSpotProperty {
  name: string;
  label: string;
  type: string;
  fieldType: string;
  groupName: string;
  options?: Array<{ label: string; value: string }>;
}

interface UseHubSpotPropertiesResult {
  contactProperties: IHubSpotProperty[];
  companyProperties: IHubSpotProperty[];
  loading: boolean;
  error: string | null;
}

export const useHubSpotProperties = (enabled: boolean): UseHubSpotPropertiesResult => {
  const [contactProperties, setContactProperties] = useState<IHubSpotProperty[]>([]);
  const [companyProperties, setCompanyProperties] = useState<IHubSpotProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchProperties = async () => {
      try {
        const [contactResult, companyResult] = await Promise.all([
          supabase.functions.invoke('hubspot-properties', {
            body: { action: 'listContactProperties' },
          }),
          supabase.functions.invoke('hubspot-properties', {
            body: { action: 'listCompanyProperties' },
          }),
        ]);

        if (cancelled) return;

        if (contactResult.error) throw contactResult.error;
        if (companyResult.error) throw companyResult.error;

        setContactProperties(contactResult.data.properties ?? []);
        setCompanyProperties(companyResult.data.properties ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch properties');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProperties();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { contactProperties, companyProperties, loading, error };
};
